import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts, getBlogCategories } from '../services/dataService';
import './Blog.css';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = getBlogCategories();

  useEffect(() => {
    const loadBlogPosts = async () => {
      setLoading(true);
      try {
        const data = await fetchBlogPosts();
        setBlogPosts(data);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogPosts();
  }, []);

  // Filter blog posts based on category and search term
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Sort posts by date (newest first)
  const sortedPosts = filteredPosts.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="blog-loading">
        <div className="container">
          <div className="loading-content">
            <div className="loading"></div>
            <p>Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-layout">
              <div className="hero-left">
                <h1 className="hero-title">
                  <span className="hero-title-line1">Our</span>
                  <span className="hero-title-line2">Expert's Blog</span>
                </h1>
              </div>
              <div className="hero-center">
                <p className="hero-subtitle">Expert tips and gardening insights</p>
              </div>
              <div className="hero-right">
                <p className="hero-description">
                  Discover valuable gardening tips, product guides, and expert advice to help you grow beautiful gardens.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="filters-section">
        <div className="container">
          <div className="filters-content">
            {/* Search Bar */}
            <div className="search-container">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Category Filters */}
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="blog-section">
        <div className="container">
          <div className="blog-header">
            <h2 className="section-title">
              {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="blog-count">
              {sortedPosts.length} article{sortedPosts.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {sortedPosts.length > 0 ? (
            <div className={`blog-grid ${sortedPosts.length <= 2 ? 'few-items' : ''}`}>
              {sortedPosts.map(post => (
                <article key={post.id} className="blog-card">
                  <div className="blog-image">
                    <img src={post.image} alt={post.title} />
                    <div className="blog-category-badge">
                      {categories.find(c => c.id === post.category)?.name}
                    </div>
                  </div>
                  <div className="blog-content">
                    <div className="blog-meta">
                      <span className="blog-date">{formatDate(post.publishedAt)}</span>
                      <span className="blog-read-time">{post.readTime}</span>
                    </div>
                    <h3 className="blog-title">{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt}</p>
                    <div className="blog-tags">
                      {post.tags.map(tag => (
                        <span key={tag} className="blog-tag">#{tag}</span>
                      ))}
                    </div>
                    <div className="blog-author">
                      <span>By {post.author}</span>
                    </div>
                    <Link to={`/blog/${post.slug || post.id}`} className="blog-link">
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="no-posts">
              <div className="no-posts-content">
                <h3>No articles found</h3>
                <p>Try adjusting your search or filter criteria.</p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Banner */}
      <section className="contact-banner">
        <div className="container">
          <div className="banner-content">
            <div className="banner-text">
              <h2 className="banner-title">Need Help with Your Garden?</h2>
              <p className="banner-description">
                Our experts are here to help you choose the right products and provide gardening advice.
              </p>
            </div>
            <div className="banner-actions">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <a href="tel:+911234567890" className="btn btn-primary">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;